// src/pages/StoryMentionsDetail.tsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Grid, Box, Alert, CircularProgress, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TagIcon from '@mui/icons-material/Tag';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

interface Action {
  action_id: string;
  action_type: string;
  account_name?: string;
  story_id?: string;
  user_id?: number;
  username?: string;
  metadata: Record<string, any>;
  created_at: string;
}

interface Detail {
  campaign_id: string;
  name: string;
  status: string;
  account_names?: string[];
  recipients_total: number;
  recipients_remaining: number;
  stories_posted: number;
  total_mentions: number;
  unique_users_mentioned: number;
  story_views: number;
  story_replies: number;
  messages_received: number;
  triggered_messages: number;
  auto_responses_sent: number;
  conversion_rate: number;
  trigger_rate: number;
  accounts_used: number;
  account_stats: Record<string, any>;
  errors: number;
  started_at?: string;
  last_activity_at?: string;
  completed_at?: string;
  progressive_mentions_info?: Record<string, any>;
  actions: Action[];
}

const StoryMentionsDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!jobId) return;
    try {
      const [statsRes, actionsRes] = await Promise.all([
        api.get(`/story_mentions/status/${jobId}`),
        api.get(`/story_mentions/actions/${jobId}`)
      ]);

      setData({
        ...statsRes.data,
        actions: actionsRes.data.actions || []
      });
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Кампания не найдена или удалена');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!jobId) {
      navigate('/story_mentions');
      return;
    }
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [jobId, navigate]);

  if (!jobId) return null;

  if (loading) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={70} />
        <Typography variant="h6" mt={3}>Загрузка статистики кампании...</Typography>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Кампания не найдена или была удалена'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/story_mentions')}>
          Назад к отметкам в сториз
        </Button>
      </Container>
    );
  }
  const actionTypeLabels: Record<string, string> = {
  story_posted: 'Сторис опубликована',
  mention_success: 'Упоминание успешно',
  mention_failed: 'Ошибка упоминания',
};
  const progress =
  data.recipients_total > 0
    ? ((data.unique_users_mentioned / data.recipients_total) * 100).toFixed(0)
    : 0;

  const accountsCount = data.account_names?.length ?? Object.keys(data.account_stats || {}).length;

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/story_mentions')} sx={{ mb: 4 }}>
        Назад к отметкам в сториз
      </Button>

      <Paper elevation={4} sx={{ p: 5, borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Typography variant="h4" fontWeight="bold" color="#1E3A8A" gutterBottom>
          Кампания #{data.campaign_id.slice(0, 8)} — {data.name}
        </Typography>
        <Typography color="#4B5563" fontSize="1.1rem" mb={4}>
          Аккаунтов: <strong>{accountsCount}</strong> ·
          Получателей: <strong>{data.recipients_total}</strong> ·
          Осталось: <strong>{data.recipients_remaining}</strong> ·
        </Typography>

        {/* Основные карточки */}
        <Box sx={{ my: 6 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#10B981">
                  {data.stories_posted}
                </Typography>
                <Typography color="text.secondary">Stories опубликовано</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#3B82F6">
                  {data.total_mentions}
                </Typography>
                <Typography color="text.secondary">Всего упоминаний</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#8B5CF6">
                  {data.unique_users_mentioned}
                </Typography>
                <Typography color="text.secondary">Уникальных пользователей</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="#F59E0B">
                  {progress}%
                </Typography>
                <Typography color="text.secondary">Прогресс</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Вовлечение */}
        <Box sx={{ my: 6 }}>
          <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
            Вовлечение
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="#6366F1">
                  {data.story_views}
                </Typography>
                <Typography color="text.secondary">Просмотры сториз</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="#10B981">
                  {data.story_replies}
                </Typography>
                <Typography color="text.secondary">Ответы на сториз</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="#F59E0B">
                  {data.messages_received}
                </Typography>
                <Typography color="text.secondary">Сообщений получено</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="#EC4899">
                  {data.auto_responses_sent}
                </Typography>
                <Typography color="text.secondary">Автоответов отправлено</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Конверсии */}
        {data.conversion_rate > 0 && (
          <Box sx={{ my: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              Конверсии
            </Typography>
            <Grid container spacing={3}>
              <Grid size={6}>
                <Paper sx={{ p: 3, bgcolor: 'white', borderRadius: 2 }}>
                  <Typography variant="h5" fontWeight="bold" color="#10B981">
                    {data.conversion_rate.toFixed(1)}%
                  </Typography>
                  <Typography color="text.secondary">Конверсия в сообщение</Typography>
                </Paper>
              </Grid>
              <Grid size={6}>
                <Paper sx={{ p: 3, bgcolor: 'white', borderRadius: 2 }}>
                  <Typography variant="h5" fontWeight="bold" color="#F59E0B">
                    {data.trigger_rate.toFixed(1)}%
                  </Typography>
                  <Typography color="text.secondary">Триггерные сообщения</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Прогрессивные упоминания */}
        {data.progressive_mentions_info && (
          <Box sx={{ my: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              Прогрессивные упоминания
            </Typography>
            <Paper sx={{ p: 3, bgcolor: '#F0FDF4' }}>
              <Typography>Текущий лимит: {data.progressive_mentions_info.current_mentions_limit?.join('–') || '—'}</Typography>
              <Typography>День кампании: {data.progressive_mentions_info.campaign_day || '—'}</Typography>
            </Paper>
          </Box>
        )}

        {/* Последние действия */}
        {data.actions.length > 0 && (
          <Box sx={{ my: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              Последние действия
            </Typography>
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Время</strong></TableCell>
                    <TableCell><strong>Тип</strong></TableCell>
                    <TableCell><strong>Аккаунт</strong></TableCell>
                    <TableCell><strong>Получатели</strong></TableCell>
                    <TableCell><strong>Детали</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.actions.slice(0, 20).map(a => (
                    <TableRow key={a.action_id}>
                      <TableCell>{new Date(a.created_at).toLocaleString('ru-RU')}</TableCell>
                      <TableCell>
                        {a.action_type === 'story_posted' && (
                            <TagIcon fontSize="small" color="primary"/>
                        )}

                        {a.action_type === 'mention_success' && (
                            <CheckCircleIcon fontSize="small" color="success"/>
                        )}

                        {a.action_type === 'mention_failed' && (
                            <ErrorIcon fontSize="small" color="error"/>
                        )}

                        {' '}
                        {actionTypeLabels[a.action_type] || a.action_type}
                      </TableCell>
                      <TableCell>{a.account_name || '—'}</TableCell>
                        <TableCell>
                            {a.username ||
                                a.user_id ||
                                a.metadata?.mentions?.join(', ') ||
                                '—'}
                        </TableCell>
                      <TableCell sx={{ color: a.metadata.error ? '#DC2626' : 'inherit' }}>
                        {a.metadata.error || a.metadata.message || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Время */}
        <Box sx={{ p: 4, bgcolor: '#F9FAFB', borderRadius: 2 }}>
          <Typography variant="h6" color="#1E40AF" gutterBottom>Время</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Запущена</Typography>
              <Typography fontWeight={600}>{data.started_at ? new Date(data.started_at).toLocaleString('ru-RU') : '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Последняя активность</Typography>
              <Typography fontWeight={600}>{data.last_activity_at ? new Date(data.last_activity_at).toLocaleString('ru-RU') : '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Завершена</Typography>
              <Typography fontWeight={600} color={data.completed_at ? '#10B981' : 'inherit'}>
                {data.completed_at ? new Date(data.completed_at).toLocaleString('ru-RU') : '—'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default StoryMentionsDetail;